import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import VoiceDictationInput from '../../components/VoiceDictationInput';
import SignatureInput from '../../components/SignatureInput';
import { FileText, Save, ArrowLeft, Printer, Plus, Trash2 } from 'lucide-react';
import { exportFillableFromDom } from '../../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVTherapyAdministration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  
  const [formData, setFormData] = useState({
    // Patient Information
    patientId: '',
    patientsName: '',
    medicalRecordNumber: '',
    diagnosis: '',
    allergies: '',
    
    // Certification and Supervision
    certificationPeriod: '',
    nurseSupervisor: '',
    
    // Nursing Orders and Goals - Therapy Device/Route
    therapyDeviceHeparinLock: false,
    therapyDeviceSubclavian: false,
    therapyDeviceHickman: false,
    therapyDevicePortACath: false,
    therapyDeviceGroshong: false,
    therapyDeviceOther: false,
    therapyDeviceOtherSpecify: '',
    typeOfInfusionDevice: '',
    
    // DME Vendor
    dmeVendor: '',
    
    // Skilled Nursing Observation
    skilledNursingObservation: '',
    
    // Catheter/Dressing/Tubing Change
    venipunctureQ72hrs: false,
    venipuncturePrn: false,
    venipunctureOther: false,
    venipunctureOtherSpecify: '',
    maintainPatent: false,
    
    // Change Dressing at IV site
    dressingChangePerAgencyProtocol: false,
    dressingChangeOther: false,
    dressingChangeOtherSpecify: '',
    
    // Change IV Tubing
    ivTubingChangeNotes: '',
    
    // Administration of IV fluids - Table
    ivFluidEntries: [
      { drugSolution: '', diluentAmtType: '', additivesAmtType: '', frequency: '', rateOfInfusion: '', duration: '' }
    ],
    
    // Irrigate Hep Lock
    irrigateUnitsPerCc: '',
    irrigateCc: '',
    irrigateHeparin: '',
    irrigateFrequency: '',
    
    // Aseptic administration
    asepticAdministration: '',
    
    // Patient Education Goals
    instructPtCaregiverParenteralNutrition: '',
    teachPtCaregiverIVAdministration: '',
    purposeOfIVTherapy: '',
    locationOfIVAccessDevice: '',
    storageOfMedications: '',
    assessmentOfIVSite: '',
    monitorForSysComplication: '',
    obtainEmergencyHelp: '',
    monitorAndRecordWeight: '',
    flushCentralLine: '',
    changeInjectionCap: '',
    changeDressingAtInsertionSite: '',
    patientEducationOther: '',
    
    // Patient/Caregiver Demonstrates
    ptCgDemonstratesAsepticTechnique: '',
    ptCgDemonstratesCorrectAdministration: '',
    ptCgVerbalizesSideEffects: '',
    ptCgVerbalizesSymptomsComplications: '',
    ptCgIdentifiesResources: '',
    ptCgDemonstratesMonitorRecord: '',
    ptCgDemonstratesChangeInjectionCap: '',
    ptCgDemonstratesAsepticDressingChange: '',
    demonstratesOther: '',
    
    // Signatures (comprehensive - typed, voice recording, handwritten)
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null },
    rnSignature: { typed: '', audioURL: null, canvasDataURL: null },
    signatureDate: '',
  });

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
  }, [formId]);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  const fetchPatients = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setPatients(data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/skilled-nursing/iv-therapy/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.form) {
        setFormData(prev => ({ ...prev, ...data.form.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load form');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIVFluidChange = (index, field, value) => {
    const newEntries = [...formData.ivFluidEntries];
    newEntries[index][field] = value;
    setFormData(prev => ({ ...prev, ivFluidEntries: newEntries }));
  };

  const addIVFluidEntry = () => {
    setFormData(prev => ({
      ...prev,
      ivFluidEntries: [...prev.ivFluidEntries, { drugSolution: '', diluentAmtType: '', additivesAmtType: '', frequency: '', rateOfInfusion: '', duration: '' }]
    }));
  };

  const removeIVFluidEntry = (index) => {
    if (formData.ivFluidEntries.length > 1) {
      const newEntries = formData.ivFluidEntries.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, ivFluidEntries: newEntries }));
    }
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const url = formId 
        ? `${API_URL}/api/skilled-nursing/iv-therapy/${formId}`
        : `${API_URL}/api/skilled-nursing/iv-therapy`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : formData.patientsName,
        formType: 'iv-therapy-administration',
        formData: formData
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form?.id) {
          navigate(`/dashboard/skilled-nursing/iv-therapy?id=${data.form.id}`);
        }
      } else {
        toast.error('Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const CheckboxField = ({ label, checked, onChange, className = '' }) => (
    <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
      <input 
        type="checkbox" 
        checked={checked || false} 
        onChange={(e) => onChange(e.target.checked)} 
        className="w-4 h-4 rounded border-gray-300"
      />
      <span className="text-sm">{label}</span>
    </label>
  );

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <FileText className="w-8 h-8 text-[#f39c12]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Intravenous Therapy Administration Sheet</h1>
                <p className="text-sm text-gray-500">Ivory Home Health Care, Inc.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => exportFillableFromDom(document.getElementById('main-content'), 'IV_Therapy.pdf')}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Company Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-xl font-bold text-[#f39c12]">Ivory Home Health Care. Inc.</h2>
            <p className="text-xs text-gray-600">Ph: (562) 349 - 0860; fax: (562) 285 – 7216</p>
            <p className="text-xs text-gray-600">Email: office@ivory_hhc.com; ivory_hhc@qbhm.net</p>
            <p className="text-xs text-gray-600">WEBSITE: www.ivory_hhc.com or www.ivory_hhc.net</p>
            <h3 className="text-lg font-bold mt-2">Intravenous Therapy Administration Sheet</h3>
          </div>

          {/* Patient Information */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-bold text-lg mb-3 text-blue-800">Patient Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Patients Name</Label>
                <select
                  value={formData.patientId}
                  onChange={(e) => {
                    const patient = patients.find(p => p.id === e.target.value);
                    handleInputChange('patientId', e.target.value);
                    if (patient) handleInputChange('patientsName', `${patient.firstName} ${patient.lastName}`);
                  }}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="">Select Patient...</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              </div>
              <VoiceDictationInput 
                label="Medical Record Number" 
                value={formData.medicalRecordNumber} 
                onChange={(e) => handleInputChange('medicalRecordNumber', e.target.value)} 
              />
              <VoiceDictationInput 
                label="Diagnosis:" 
                value={formData.diagnosis} 
                onChange={(e) => handleInputChange('diagnosis', e.target.value)} 
              />
              <VoiceDictationInput 
                label="Allergies:" 
                value={formData.allergies} 
                onChange={(e) => handleInputChange('allergies', e.target.value)} 
              />
            </div>
          </div>

          {/* Certification and Supervision */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-bold text-lg mb-3 text-green-800">Certification and Supervision</h4>
            <div className="grid grid-cols-2 gap-4">
              <VoiceDictationInput 
                label="Certification Period:" 
                value={formData.certificationPeriod} 
                onChange={(e) => handleInputChange('certificationPeriod', e.target.value)} 
              />
              <VoiceDictationInput 
                label="Nurse Supervisor:" 
                value={formData.nurseSupervisor} 
                onChange={(e) => handleInputChange('nurseSupervisor', e.target.value)} 
              />
            </div>
          </div>

          {/* Nursing Orders and Goals */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h4 className="font-bold text-lg mb-3 text-purple-800">Nursing Orders and Goals</h4>
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Therapy Device/Route:</Label>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <CheckboxField label="Heparin Lock" checked={formData.therapyDeviceHeparinLock} onChange={(v) => handleInputChange('therapyDeviceHeparinLock', v)} />
                  <CheckboxField label="Subclavian" checked={formData.therapyDeviceSubclavian} onChange={(v) => handleInputChange('therapyDeviceSubclavian', v)} />
                  <CheckboxField label="Hickman" checked={formData.therapyDeviceHickman} onChange={(v) => handleInputChange('therapyDeviceHickman', v)} />
                  <CheckboxField label="Port-A-Cath" checked={formData.therapyDevicePortACath} onChange={(v) => handleInputChange('therapyDevicePortACath', v)} />
                  <CheckboxField label="Groshong" checked={formData.therapyDeviceGroshong} onChange={(v) => handleInputChange('therapyDeviceGroshong', v)} />
                  <CheckboxField label="Other" checked={formData.therapyDeviceOther} onChange={(v) => handleInputChange('therapyDeviceOther', v)} />
                </div>
                {formData.therapyDeviceOther && (
                  <VoiceDictationInput 
                    value={formData.therapyDeviceOtherSpecify} 
                    onChange={(e) => handleInputChange('therapyDeviceOtherSpecify', e.target.value)}
                    placeholder="Specify other device..."
                    className="mt-2"
                  />
                )}
              </div>
              <VoiceDictationInput 
                label="Type of infusion device:" 
                value={formData.typeOfInfusionDevice} 
                onChange={(e) => handleInputChange('typeOfInfusionDevice', e.target.value)} 
              />
            </div>
          </div>

          {/* DME Vendor */}
          <div className="border rounded-lg p-4 bg-yellow-50">
            <VoiceDictationInput 
              label="DME Vendor:" 
              value={formData.dmeVendor} 
              onChange={(e) => handleInputChange('dmeVendor', e.target.value)} 
            />
          </div>

          {/* Skilled Nursing Observation */}
          <div className="border rounded-lg p-4 bg-teal-50">
            <h4 className="font-bold text-lg mb-3 text-teal-800">Skilled Nursing Observation</h4>
            <p className="text-sm text-gray-600 mb-2">Observation of fluid and electrolyte balance, S/S of infection, phlebitis, and clotted catheter. Maintain patent, infection free IV access line.</p>
            <VoiceDictationInput 
              value={formData.skilledNursingObservation} 
              onChange={(e) => handleInputChange('skilledNursingObservation', e.target.value)}
              rows={3}
            />
          </div>

          {/* Catheter/Dressing/Tubing Change */}
          <div className="border rounded-lg p-4 bg-orange-50">
            <h4 className="font-bold text-lg mb-3 text-orange-800">Catheter/Dressing/Tubing Change</h4>
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Venipuncture for IV cannula change:</Label>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <CheckboxField label="q72hrs" checked={formData.venipunctureQ72hrs} onChange={(v) => handleInputChange('venipunctureQ72hrs', v)} />
                  <CheckboxField label="prn" checked={formData.venipuncturePrn} onChange={(v) => handleInputChange('venipuncturePrn', v)} />
                  <CheckboxField label="other" checked={formData.venipunctureOther} onChange={(v) => handleInputChange('venipunctureOther', v)} />
                  {formData.venipunctureOther && (
                    <VoiceDictationInput 
                      value={formData.venipunctureOtherSpecify} 
                      onChange={(e) => handleInputChange('venipunctureOtherSpecify', e.target.value)}
                      placeholder="Specify..."
                      className="w-48"
                    />
                  )}
                </div>
              </div>
              <CheckboxField label="Maintain patent" checked={formData.maintainPatent} onChange={(v) => handleInputChange('maintainPatent', v)} />
              <div>
                <Label className="font-semibold">Change Dressing at IV site:</Label>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <CheckboxField label="per agency protocol" checked={formData.dressingChangePerAgencyProtocol} onChange={(v) => handleInputChange('dressingChangePerAgencyProtocol', v)} />
                  <CheckboxField label="other" checked={formData.dressingChangeOther} onChange={(v) => handleInputChange('dressingChangeOther', v)} />
                  {formData.dressingChangeOther && (
                    <VoiceDictationInput 
                      value={formData.dressingChangeOtherSpecify} 
                      onChange={(e) => handleInputChange('dressingChangeOtherSpecify', e.target.value)}
                      placeholder="Specify..."
                      className="w-48"
                    />
                  )}
                </div>
              </div>
              <VoiceDictationInput 
                label="Change IV Tubing q 48-72hrs and prn:" 
                value={formData.ivTubingChangeNotes} 
                onChange={(e) => handleInputChange('ivTubingChangeNotes', e.target.value)} 
              />
            </div>
          </div>

          {/* Administration of IV fluids Table */}
          <div className="border rounded-lg p-4 bg-red-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-lg text-red-800">Administration of IV fluids</h4>
              <Button onClick={addIVFluidEntry} size="sm" className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Plus className="w-4 h-4 mr-1" /> Add Entry
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-200">
                  <tr>
                    <th className="p-2 text-left">Drug/Solution</th>
                    <th className="p-2 text-left">Diluent (amt/type)</th>
                    <th className="p-2 text-left">Additives (amt/type)</th>
                    <th className="p-2 text-left">Frequency</th>
                    <th className="p-2 text-left">Rate of Infusion</th>
                    <th className="p-2 text-left">Duration</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.ivFluidEntries.map((entry, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.drugSolution} 
                          onChange={(e) => handleIVFluidChange(index, 'drugSolution', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.diluentAmtType} 
                          onChange={(e) => handleIVFluidChange(index, 'diluentAmtType', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.additivesAmtType} 
                          onChange={(e) => handleIVFluidChange(index, 'additivesAmtType', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.frequency} 
                          onChange={(e) => handleIVFluidChange(index, 'frequency', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.rateOfInfusion} 
                          onChange={(e) => handleIVFluidChange(index, 'rateOfInfusion', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        <VoiceDictationInput 
                          value={entry.duration} 
                          onChange={(e) => handleIVFluidChange(index, 'duration', e.target.value)}
                        />
                      </td>
                      <td className="p-1">
                        {formData.ivFluidEntries.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeIVFluidEntry(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Irrigate Hep Lock */}
          <div className="border rounded-lg p-4 bg-indigo-50">
            <h4 className="font-bold text-lg mb-3 text-indigo-800">Irrigate Hep Lock</h4>
            <div className="grid grid-cols-4 gap-4">
              <VoiceDictationInput label="Units per cc" value={formData.irrigateUnitsPerCc} onChange={(e) => handleInputChange('irrigateUnitsPerCc', e.target.value)} />
              <VoiceDictationInput label="#cc" value={formData.irrigateCc} onChange={(e) => handleInputChange('irrigateCc', e.target.value)} />
              <VoiceDictationInput label="Heparin" value={formData.irrigateHeparin} onChange={(e) => handleInputChange('irrigateHeparin', e.target.value)} />
              <VoiceDictationInput label="Frequency" value={formData.irrigateFrequency} onChange={(e) => handleInputChange('irrigateFrequency', e.target.value)} />
            </div>
            <VoiceDictationInput 
              label="Aseptic administration of drug/solution:" 
              value={formData.asepticAdministration} 
              onChange={(e) => handleInputChange('asepticAdministration', e.target.value)}
              className="mt-4"
              rows={2}
            />
          </div>

          {/* Patient Education */}
          <div className="border rounded-lg p-4 bg-cyan-50">
            <h4 className="font-bold text-lg mb-3 text-cyan-800">Patient Education</h4>
            <div className="space-y-3">
              <VoiceDictationInput label="Instruct pt/caregiver in parenteral nutrition:" value={formData.instructPtCaregiverParenteralNutrition} onChange={(e) => handleInputChange('instructPtCaregiverParenteralNutrition', e.target.value)} />
              <VoiceDictationInput label="Teach pt/caregiver to properly administer IV solutions/medications using aseptic technique, troubleshooting, infusions, and equipment:" value={formData.teachPtCaregiverIVAdministration} onChange={(e) => handleInputChange('teachPtCaregiverIVAdministration', e.target.value)} rows={2} />
              <VoiceDictationInput label="Purpose of IV therapy related to disease process and common side effects of medications:" value={formData.purposeOfIVTherapy} onChange={(e) => handleInputChange('purposeOfIVTherapy', e.target.value)} />
              <VoiceDictationInput label="Location of IV ACCESS DEVICE:" value={formData.locationOfIVAccessDevice} onChange={(e) => handleInputChange('locationOfIVAccessDevice', e.target.value)} />
              <VoiceDictationInput label="Storage of medications:" value={formData.storageOfMedications} onChange={(e) => handleInputChange('storageOfMedications', e.target.value)} />
              <VoiceDictationInput label="Assessment of IV site for S/S of complications including redness/heat, pain/tenderness, swelling, decreased flow rate, leaking/exudate at site:" value={formData.assessmentOfIVSite} onChange={(e) => handleInputChange('assessmentOfIVSite', e.target.value)} rows={2} />
              <VoiceDictationInput label="Monitor for S/S of systemic complications:" value={formData.monitorForSysComplication} onChange={(e) => handleInputChange('monitorForSysComplication', e.target.value)} />
              <VoiceDictationInput label="Obtain emergency help if needed:" value={formData.obtainEmergencyHelp} onChange={(e) => handleInputChange('obtainEmergencyHelp', e.target.value)} />
              <VoiceDictationInput label="Monitor and record weight, intake and output, temperature:" value={formData.monitorAndRecordWeight} onChange={(e) => handleInputChange('monitorAndRecordWeight', e.target.value)} />
              <VoiceDictationInput label="Flush central line:" value={formData.flushCentralLine} onChange={(e) => handleInputChange('flushCentralLine', e.target.value)} />
              <VoiceDictationInput label="Change injection cap:" value={formData.changeInjectionCap} onChange={(e) => handleInputChange('changeInjectionCap', e.target.value)} />
              <VoiceDictationInput label="Change dressing at insertion site:" value={formData.changeDressingAtInsertionSite} onChange={(e) => handleInputChange('changeDressingAtInsertionSite', e.target.value)} />
              <VoiceDictationInput label="Other:" value={formData.patientEducationOther} onChange={(e) => handleInputChange('patientEducationOther', e.target.value)} />
            </div>
          </div>

          {/* Patient/Caregiver Demonstrates */}
          <div className="border rounded-lg p-4 bg-lime-50">
            <h4 className="font-bold text-lg mb-3 text-lime-800">Patient/Caregiver Demonstrates</h4>
            <div className="space-y-3">
              <VoiceDictationInput label="Pt/caregiver demonstrates aseptic technique in handling IV solution and tubing supplies:" value={formData.ptCgDemonstratesAsepticTechnique} onChange={(e) => handleInputChange('ptCgDemonstratesAsepticTechnique', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver is able to demonstrate correct administration of IV medication or solution:" value={formData.ptCgDemonstratesCorrectAdministration} onChange={(e) => handleInputChange('ptCgDemonstratesCorrectAdministration', e.target.value)} />
              <VoiceDictationInput label="Pt/Caregiver verbalizes common side effects of medication/solution and action to take if it occurs:" value={formData.ptCgVerbalizesSideEffects} onChange={(e) => handleInputChange('ptCgVerbalizesSideEffects', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver verbalizes S/S of common local systemic complications and actions to take if occur:" value={formData.ptCgVerbalizesSymptomsComplications} onChange={(e) => handleInputChange('ptCgVerbalizesSymptomsComplications', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver identifies resources to call for help:" value={formData.ptCgIdentifiesResources} onChange={(e) => handleInputChange('ptCgIdentifiesResources', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver demonstrates ability to monitor and record temperature, weight, intake and output:" value={formData.ptCgDemonstratesMonitorRecord} onChange={(e) => handleInputChange('ptCgDemonstratesMonitorRecord', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver demonstrates ability to change injection cap:" value={formData.ptCgDemonstratesChangeInjectionCap} onChange={(e) => handleInputChange('ptCgDemonstratesChangeInjectionCap', e.target.value)} />
              <VoiceDictationInput label="Pt/caregiver demonstrates aseptic technique in dressing change and site care:" value={formData.ptCgDemonstratesAsepticDressingChange} onChange={(e) => handleInputChange('ptCgDemonstratesAsepticDressingChange', e.target.value)} />
              <VoiceDictationInput label="Other:" value={formData.demonstratesOther} onChange={(e) => handleInputChange('demonstratesOther', e.target.value)} />
            </div>
          </div>

          {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
          <div className="border rounded-lg p-4 bg-gray-100">
            <h4 className="font-bold text-lg mb-3">SIGNATURES</h4>
            <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-3">
                <SignatureInput 
                  label="Patient Signature" 
                  value={formData.patientSignature} 
                  onChange={(value) => handleInputChange('patientSignature', value)} 
                />
              </div>
              <div className="space-y-3">
                <SignatureInput 
                  label="RN Signature" 
                  value={formData.rnSignature} 
                  onChange={(value) => handleInputChange('rnSignature', value)} 
                />
              </div>
              <div className="space-y-3">
                <VoiceDictationInput label="Date" value={formData.signatureDate} onChange={(e) => handleInputChange('signatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IVTherapyAdministration;
